import type { InputHTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/utils";

export type InputSize = "xs" | "md";

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & {
  label?: string;
  error?: string;
  size?: InputSize;
  /** Content rendered inside the input on the right (e.g. unit suffix) */
  suffix?: ReactNode;
  wrapperClassName?: string;
};

export function Input({
  label,
  error,
  size = "md",
  suffix,
  className = "",
  wrapperClassName = "",
  id,
  ...props
}: InputProps) {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className={cn("flex flex-col gap-1.5", wrapperClassName)}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-content-secondary">
          {label}
        </label>
      )}
      {suffix ? (
        <div
          className={cn(
            "flex items-center border text-white",
            size === "xs" ? "rounded bg-surface-700" : "rounded-lg bg-surface-800",
            "transition-colors duration-200",
            "focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-transparent",
            error ? "border-error-500" : "border-surface-700 hover:border-surface-600",
          )}
        >
          <input
            id={inputId}
            className={cn(
              "w-full bg-transparent text-white border-none outline-none",
              size === "xs" ? "px-1.5 py-0.5 text-xs" : "px-4 py-2 text-sm",
              "placeholder-content-muted",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              className,
            )}
            {...props}
          />
          <span
            className={cn(
              "shrink-0 text-surface-400 pointer-events-none",
              size === "xs" ? "pr-1.5 text-xs" : "pr-3 text-sm",
            )}
          >
            {suffix}
          </span>
        </div>
      ) : (
        <input
          id={inputId}
          className={cn(
            "w-full border text-white",
            size === "xs"
              ? "px-1.5 py-0.5 rounded text-xs bg-surface-700"
              : "px-4 py-2 rounded-lg text-sm bg-surface-800",
            "placeholder-content-muted transition-colors duration-200",
            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            error ? "border-error-500" : "border-surface-700 hover:border-surface-600",
            className,
          )}
          {...props}
        />
      )}
      {error && <p className="text-sm text-error-500">{error}</p>}
    </div>
  );
}
